import React from "react";

const ColorPicker: React.FC<{ 
  editor: any; 
  type: 'text' | 'background'; 
  title: string; 
  icon: string;
  openColorPicker: string | null;
  setOpenColorPicker: (id: string | null) => void;
}> = ({ editor, type, title, icon, openColorPicker, setOpenColorPicker }) => {
  const [customColor, setCustomColor] = React.useState('#000000');
  const [isActive, setIsActive] = React.useState(false);
  
  const dropdownId = `color-picker-${type}`;
  const isOpen = openColorPicker === dropdownId;
  
  const colors = [
    { name: 'Black', value: '#000000' },
    { name: 'Dark Gray', value: '#374151' },
    { name: 'Gray', value: '#6B7280' },
    { name: 'Light Gray', value: '#9CA3AF' },
    { name: 'White', value: '#FFFFFF' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Orange', value: '#F97316' },
    { name: 'Yellow', value: '#EAB308' },
    { name: 'Green', value: '#22C55E' },
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Indigo', value: '#6366F1' },
    { name: 'Purple', value: '#A855F7' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Cyan', value: '#06B6D4' },
    { name: 'Lime', value: '#84CC16' },
    { name: 'Rose', value: '#F43F5E' },
  ];

  const backgroundColors = [
    { name: 'No Background', value: 'transparent' },
    { name: 'Light Gray', value: '#F3F4F6' },
    { name: 'Light Red', value: '#FEE2E2' },
    { name: 'Light Orange', value: '#FED7AA' },
    { name: 'Light Yellow', value: '#FEF3C7' },
    { name: 'Light Green', value: '#DCFCE7' },
    { name: 'Light Blue', value: '#DBEAFE' },
    { name: 'Light Indigo', value: '#E0E7FF' },
    { name: 'Light Purple', value: '#F3E8FF' },
    { name: 'Light Pink', value: '#FCE7F3' },
    { name: 'Light Cyan', value: '#CFFAFE' },
    { name: 'Light Lime', value: '#ECFCCB' },
    { name: 'Light Rose', value: '#FFE4E6' },
    { name: 'Light Amber', value: '#FEF3C7' },
    { name: 'Light Emerald', value: '#D1FAE5' },
    { name: 'Light Sky', value: '#E0F2FE' },
  ];

  const colorOptions = type === 'text' ? colors : backgroundColors;

  // Function to check if color/highlight is active
  const checkActiveState = React.useCallback(() => {
    if (type === 'text') {
      // Check if any text color is applied (not default black)
      return editor.isActive('textStyle', { color: true }) || 
             editor.getAttributes('textStyle').color !== undefined;
    } else {
      // Check if highlight is active
      return editor.isActive('highlight');
    }
  }, [editor, type]);

  // Update active state when editor changes
  React.useEffect(() => {
    const updateActiveState = () => {
      setIsActive(checkActiveState());
    };

    if (editor) {
      editor.on('selectionUpdate', updateActiveState);
      editor.on('transaction', updateActiveState);
      
      // Initial update
      updateActiveState();
    }

    return () => {
      if (editor) {
        editor.off('selectionUpdate', updateActiveState);
        editor.off('transaction', updateActiveState);
      }
    };
  }, [editor, checkActiveState]);

  const applyColor = (color: string) => {
    if (type === 'text') {
      if (color === 'transparent' || color === '#000000') {
        editor.chain().focus().unsetColor().run();
      } else {
        editor.chain().focus().setColor(color).run();
      }
    } else {
      if (color === 'transparent') {
        editor.chain().focus().unsetHighlight().run();
      } else {
        editor.chain().focus().setHighlight({ color }).run();
      }
    }
    setOpenColorPicker(null);
  };

  const applyCustomColor = () => {
    applyColor(customColor);
  };

  const toggleDropdown = () => {
    setOpenColorPicker(isOpen ? null : dropdownId);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isOpen && !target.closest(`[data-dropdown-id="${dropdownId}"]`)) {
        setOpenColorPicker(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, dropdownId, setOpenColorPicker]);

  const renderColorSwatch = (color: { name: string; value: string }, size: 'large' | 'small', index: number) => {
    const isTransparent = color.value === 'transparent';
    
    return (
      <div
        key={index}
        className={`color-swatch ${size} ${isTransparent ? 'transparent' : ''}`}
        onClick={() => applyColor(color.value)}
        style={{
          backgroundColor: isTransparent ? 'transparent' : color.value,
        }}
        title={color.name}
      >
        {isTransparent && <i className="ti ti-x text-danger"></i>}
      </div>
    );
  };

  return (
    <div className={`dropdown color-picker-dropdown color-picker-${type}`} data-dropdown-id={dropdownId}>
      <button
        onClick={toggleDropdown}
        className={`
          btn btn-sm d-flex align-items-center justify-content-center p-2 position-relative
          ${isActive ? 'btn-secondary' : 'btn-outline-secondary'}
        `}
        title={title}
      >
        <i className={`${icon} fs-16`}></i>
        <i className="ti ti-chevron-down ms-1 fs-10"></i>
      </button>
      
      {isOpen && (
        <div className="dropdown-menu show position-absolute mt-1 p-3" style={{ minWidth: '280px', zIndex: 1000 }}>
          {/* Quick Colors */}
          <div className="mb-3">
            <label className="form-label fs-12 mb-2 fw-semibold">Quick Colors</label>
            <div className="color-grid quick-colors">
              {colorOptions.slice(0, 8).map((color, index) => 
                renderColorSwatch(color, 'large', index)
              )}
            </div>
          </div>

          {/* All Colors */}
          <div className="mb-3">
            <label className="form-label fs-12 mb-2 fw-semibold">All Colors</label>
            <div className="color-grid all-colors">
              {colorOptions.map((color, index) => 
                renderColorSwatch(color, 'small', index)
              )}
            </div>
          </div>
          
          <hr className="my-2" />
          
          {/* Custom Color Picker */}
          <div className="mb-2">
            <label className="form-label fs-12 mb-2 fw-semibold">Custom Color</label>
            <div className="d-flex gap-2 align-items-center">
              <input
                type="color"
                className="form-control form-control-sm"
                style={{ width: '50px', height: '35px' }}
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                title="Pick custom color"
              />
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="#000000"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                maxLength={7}
              />
              <button
                className="btn btn-sm btn-primary"
                onClick={applyCustomColor}
                title="Apply custom color"
              >
                <i className="ti ti-check fs-12"></i>
              </button>
            </div>
          </div>

          {/* Remove Color Button */}
          {type === 'background' && (
            <>
              <hr className="my-2" />
              <button
                onClick={() => applyColor('transparent')}
                className="btn btn-outline-secondary btn-sm w-100"
              >
                <i className="ti ti-x me-2"></i>
                Remove Background Color
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
